// src/components/modal/product/ProductModal.jsx
import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { X } from "lucide-react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogClose,
} from "components/ui/dialog";

// tamanhos padrão (caso não venha do App)
const DEFAULT_SIZES = [
  { key: "200", label: "200 ml", basePrice: 12.0, included: 2 },
  { key: "240", label: "240 ml", basePrice: 14.0, included: 3 },
  { key: "300", label: "300 ml", basePrice: 16.0, included: 3 },
];

const formatBRL = (n) =>
  (typeof n === "number" ? n : Number(n || 0)).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 2,
  });

export default function ProductModal({
  isOpen,
  onClose,
  product,
  creating = "N", // S|N
  hasExtraIngredients = "N",
  hasExtraProducts = "N",
  addToCart,
  sizes = DEFAULT_SIZES,
  onOpenCart,
  cart = [],
}) {
  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        {/* header/imagem */}
        <div className="relative">
          {/* Imagem clicável — abre visualização maior */}
          <Dialog>
            <DialogTrigger asChild>
              <button
                className="p-0 m-0 w-full rounded-t-2xl overflow-hidden relative"
                aria-label={`Abrir imagem ${product.name}`}
              >
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-64 object-cover cursor-zoom-in"
                  width={286}
                  height={192}
                />
                {/* 💧 Marca d'água */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-white text-xl sm:text-2xl font-bold opacity-30 transform rotate-[-20deg] select-none">
                    Imagem meramente ilustrativa
                  </p>
                </div>
              </button>
            </DialogTrigger>

            <DialogContent
              className="bg-black p-4 sm:p-6 max-w-7xl w-full rounded-none border-none z-[11001]"
              overlayClassName="z-[11000]"
            >
              <div className="relative flex items-center justify-center">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="max-h-[85vh] w-auto max-w-full object-contain"
                />

                {/* 💧 Marca d'água na visualização ampliada */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-white text-3xl sm:text-4xl md:text-5xl font-bold opacity-25 transform rotate-[-20deg] select-none">
                    Imagem meramente ilustrativa
                  </p>
                </div>

                {/* Botão fechar — visível sobre o fundo escuro */}
                <DialogClose asChild>
                  <button
                    aria-label="Fechar visualização da imagem"
                    className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white rounded-full p-2 transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </DialogClose>
              </div>
              <p className="text-sm text-white/70 mt-4 text-center">
                {product.name}
              </p>
            </DialogContent>
          </Dialog>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-white/90 hover:bg-white rounded-full p-2 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <ProductModalBody
          creating={creating}
          hasExtraIngredients={hasExtraIngredients}
          hasExtraProducts={hasExtraProducts}
          product={product}
          addToCart={addToCart}
          onClose={onClose}
          sizes={sizes}
          onOpenCart={onOpenCart}
          cart={cart}
        />
      </div>
    </div>
  );
}

function ProductModalBody({
  creating,
  hasExtraIngredients,
  hasExtraProducts,
  product,
  addToCart,
  onClose,
  sizes,
  onOpenCart,
  cart = [],
}) {
  // normaliza ingredientes
  const baseIngredients = useMemo(
    () =>
      (product?.ingredients ?? []).map((n) => ({
        id: String(n.id),
        name: n.name,
        price: Number(n.price ?? 0),
      })),
    [product]
  );

  const extraIngredients = useMemo(() => {
    const extras = product?.extraIngredients ?? product?.extras ?? [];
    return extras.map((n) => ({
      id: String(n.id),
      name: n.name,
      price: Number(n.price ?? 0),
    }));
  }, [product]);

  // normaliza produtos
  const baseProdutos = useMemo(
    () =>
      (product?.products ?? []).map((n) => ({
        id: String(n.code),
        name: n.name,
        price: Number(n.price ?? 0),
        qty: Number(n.qty ?? 99),
      })),
    [product]
  );

  const extraProducts = useMemo(() => {
    const extras = product?.extraProducts ?? [];
    return extras.map((n) => ({
      id: String(n.code),
      name: n.name,
      price: Number(n.price ?? 0),
      qty: Number(n.qty ?? 99),
    }));
  }, [product]);

  // estado local
  const [qty, setQty] = useState(1);
  const [comments, setComments] = useState("");
  const [sizeKey, setSizeKey] = useState(sizes[0]?.key ?? "200");

  // Quantidade de cada produto (id) que JÁ ESTÁ no carrinho.
  // Calculado direto do `cart` (fonte de verdade) — o `bonfim_products_tracking`
  // do localStorage pode ficar desatualizado (ex.: carrinho limpo/sincronizado
  // sem recalcular o rastreamento) e travava a seleção indevidamente.
  // Mesma regra do updateProductsTracking (App.js): produto simples/combo conta
  // pela quantidade do item; sub-produtos de combo contam qty × quantidade do combo.
  // NÃO inclui as seleções em andamento neste modal (somadas por linha, abaixo).
  const productsQtyInCart = useMemo(() => {
    const qtyMap = {};
    (cart ?? []).forEach((cartItem) => {
      const cartItemQty = Number(cartItem.quantity ?? 1);

      if (cartItem.product_id) {
        const productId = String(cartItem.product_id);
        qtyMap[productId] = (qtyMap[productId] || 0) + cartItemQty;
      }

      if (Array.isArray(cartItem.selected_produtos)) {
        cartItem.selected_produtos.forEach((prod) => {
          const prodQty = Number(prod.qty ?? 0);
          if (prod.id && prodQty > 0) {
            const productId = String(prod.id);
            qtyMap[productId] = (qtyMap[productId] || 0) + prodQty * cartItemQty;
          }
        });
      }
    });
    return qtyMap;
  }, [cart]);

  // Calcula quantidade já no carrinho para este produto
  const qtyInCart = product ? productsQtyInCart[String(product.id)] || 0 : 0;

  // Calcula estoque disponível real (total - já no carrinho)
  const availableStock = useMemo(() => {
    const totalStock = Number(product?.quantity ?? 99);
    return Math.max(0, totalStock - qtyInCart);
  }, [product?.quantity, qtyInCart]);

  // Atualiza qty se exceder o estoque disponível
  useEffect(() => {
    if (availableStock > 0 && qty > availableStock) {
      setQty(availableStock);
    } else if (availableStock === 0 && qty > 0) {
      setQty(0);
    }
  }, [availableStock, qty]);

  // quantidades para ingredientes base e produtos base
  const [baseQty, setBaseQty] = useState({}); // { [id]: number }
  const [baseProductQty, setBaseProductQty] = useState({}); // { [id]: number }

  // quantidades de extras
  const [extraQty, setExtraQty] = useState({}); // { [id]: number }
  const [extraProductQty, setExtraProductQty] = useState({}); // { [id]: number }

  // Estoque de um sub-produto (seleção/extra) considerando carrinho + o que já
  // foi selecionado neste modal (o mesmo produto pode estar em "seleção" e "extras").
  const getSubProductStock = (id, maxQty) => {
    const inCart = productsQtyInCart[id] || 0;
    const selectedHere = (baseProductQty[id] ?? 0) + (extraProductQty[id] ?? 0);
    return {
      inCart,
      // esgotado de verdade = nada sobrando nem antes desta seleção
      isOutOfStock: maxQty - inCart <= 0,
      // quantos ainda podem ser adicionados neste modal
      remaining: Math.max(0, maxQty - inCart - selectedHere),
    };
  };

  // modal bonitão de confirmação
  const [showConfirm, setShowConfirm] = useState(false);

  const sizeInfo = useMemo(
    () => sizes.find((s) => s.key === sizeKey) ?? sizes[0],
    [sizes, sizeKey]
  );

  // controle para limitar seleção quando atingir a franquia
  const baseIncludedCount = Number(sizeInfo?.included ?? 0);
  const baseSelectedCount = Object.values(baseQty).reduce(
    (acc, q) => acc + q,
    0
  );
  const baseLimitReached =
    baseIncludedCount > 0 && baseSelectedCount >= baseIncludedCount;

  const baseProductIncludedCount = Number(sizeInfo?.includedProduct ?? 0);
  const baseProductSelectedCount = Object.values(baseProductQty).reduce(
    (acc, q) => acc + q,
    0
  );
  const baseProductLimitReached =
    baseProductIncludedCount > 0 &&
    baseProductSelectedCount >= baseProductIncludedCount;

  // 🎯 Validação de combo: verifica se a quantidade selecionada é EXATAMENTE igual à obrigatória
  const isComboValid = useMemo(() => {
    // Se não é combo (creating !== "S"), sempre válido
    if (creating !== "S") return true;

    // Verifica ingredientes (se houver)
    const hasIngredientRequirement = baseIncludedCount > 0;
    const ingredientsValid =
      !hasIngredientRequirement || baseSelectedCount === baseIncludedCount;

    // Verifica produtos (se houver)
    const hasProductRequirement = baseProductIncludedCount > 0;
    const productsValid =
      !hasProductRequirement ||
      baseProductSelectedCount === baseProductIncludedCount;

    return ingredientsValid && productsValid;
  }, [
    creating,
    baseIncludedCount,
    baseSelectedCount,
    baseProductIncludedCount,
    baseProductSelectedCount,
  ]);

  // cálculo total
  const total = useMemo(() => {
    if (creating !== "S") {
      return Number(product?.price ?? 0) * qty;
    }

    const base = Number(sizeInfo?.basePrice ?? product?.price ?? 0);

    // ingredientes base - NUNCA são cobrados (estão incluídos no limite)
    // Apenas os extras (extraIngredients) que são cobrados

    // ingredientes extras (qty * preço)
    const extrasCost = extraIngredients.reduce(
      (acc, ing) => acc + (extraQty[ing.id] ?? 0) * Number(ing.price || 0),
      0
    );

    // produtos base - NUNCA são cobrados (estão incluídos no limite)
    // Apenas os extras (extraProducts) que são cobrados

    // produtos extras (qty * preço)
    const extraProductsCost = extraProducts.reduce(
      (acc, p) => acc + (extraProductQty[p.id] ?? 0) * Number(p.price || 0),
      0
    );

    return (base + extrasCost + extraProductsCost) * qty;
  }, [
    creating,
    product?.price,
    qty,
    sizeInfo,
    extraIngredients,
    extraQty,
    extraProducts,
    extraProductQty,
  ]);

  // helpers de quantidade para ingredientes base
  const incBase = (id) => {
    const currentTotal = Object.values(baseQty).reduce((acc, q) => acc + q, 0);
    const included = Number(sizeInfo?.included ?? 0);

    // Só permite incrementar se o total ainda não atingiu o limite
    if (currentTotal < included) {
      setBaseQty((prev) => ({
        ...prev,
        [id]: (prev[id] ?? 0) + 1,
      }));
    }
  };

  const decBase = (id) =>
    setBaseQty((prev) => ({
      ...prev,
      [id]: Math.max(0, (prev[id] ?? 0) - 1),
    }));

  // helpers de quantidade para produtos base
  const incBaseProduct = (id) => {
    const currentTotal = Object.values(baseProductQty).reduce(
      (acc, q) => acc + q,
      0
    );
    const included = Number(sizeInfo?.includedProduct ?? 0);

    // Só permite incrementar se o total ainda não atingiu o limite
    if (currentTotal < included) {
      setBaseProductQty((prev) => ({
        ...prev,
        [id]: (prev[id] ?? 0) + 1,
      }));
    }
  };

  const decBaseProduct = (id) =>
    setBaseProductQty((prev) => ({
      ...prev,
      [id]: Math.max(0, (prev[id] ?? 0) - 1),
    })); // helpers de quantidade para extras (verde/vermelho)
  const incExtra = (id) =>
    setExtraQty((prev) => ({
      ...prev,
      [id]: (prev[id] ?? 0) + 1,
    }));

  const decExtra = (id) =>
    setExtraQty((prev) => ({
      ...prev,
      [id]: Math.max(0, (prev[id] ?? 0) - 1),
    }));

  const incExtraProduct = (id) =>
    setExtraProductQty((prev) => ({
      ...prev,
      [id]: (prev[id] ?? 0) + 1,
    }));

  const decExtraProduct = (id) =>
    setExtraProductQty((prev) => ({
      ...prev,
      [id]: Math.max(0, (prev[id] ?? 0) - 1),
    }));

  // adicionar ao carrinho
  const handleAdd = () => {
    // Verifica se o carrinho estava vazio ANTES de adicionar
    const wasCartEmpty = cart.length === 0;

    if (creating === "S") {
      // ingredientes base com quantidade (SEM marcar como extra)
      const selectedBase = baseIngredients
        .map((i) => ({
          id: i.id,
          name: i.name,
          price: i.price,
          qty: baseQty[i.id] ?? 0,
        }))
        .filter((i) => i.qty > 0);

      // ingredientes extras com quantidade (MARCADOS como extra)
      const selectedExtras = extraIngredients
        .map((i) => ({
          id: i.id,
          name: i.name,
          price: i.price,
          qty: extraQty[i.id] ?? 0,
          isExtra: true, // Marca como extra
        }))
        .filter((i) => i.qty > 0);

      const unitPrice =
        qty > 0 ? Number((total / qty).toFixed(2)) : Number(product.price || 0);

      // produtos base com quantidade (SEM marcar como extra)
      const selectedProducts = baseProdutos
        .map((p) => ({
          id: p.id,
          name: p.name,
          price: p.price,
          qty: baseProductQty[p.id] ?? 0,
        }))
        .filter((p) => p.qty > 0);

      // extras produtos selecionados (MARCADOS como extra)
      const selectedExtraProducts = extraProducts
        .map((p) => ({
          id: p.id,
          name: p.name,
          price: p.price,
          qty: extraProductQty[p.id] ?? 0,
          isExtra: true, // Marca como extra
        }))
        .filter((p) => p.qty > 0);

      // Junta ingredientes base com extras, mas mantém a marcação
      const allIngredients = [...selectedBase, ...selectedExtras];

      // Junta produtos base com extras, mas mantém a marcação
      const allProducts = [...selectedProducts, ...selectedExtraProducts];

      addToCart(
        {
          ...product,
          price: unitPrice,
          image_url: product.image_url,
        },
        qty,
        comments,
        allIngredients,
        allProducts
      );
    } else {
      addToCart(product, qty, comments, product?.ingredients ?? [], []);
    }

    // Só abre modal de confirmação se o carrinho estava vazio
    if (wasCartEmpty) {
      setShowConfirm(true);
    } else {
      // Se já tinha itens, apenas fecha o modal do produto
      onClose?.();
    }
  };

  const handleConfirmContinue = () => {
    setShowConfirm(false);
    onClose?.();
  };

  const handleConfirmGoToCart = () => {
    setShowConfirm(false);
    onClose?.();
    onOpenCart?.();
  };

  return (
    <div className="p-6 relative">
      {/* conteúdo principal */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-800">{product.name}</h2>
          <p className="text-gray-600 mt-1">{product.description}</p>

          {/* Aviso de estoque */}
          {product.quantity !== undefined && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {/* Badge de quantidade no carrinho */}
              {qtyInCart > 0 && (
                <div className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg">
                  <svg
                    className="w-4 h-4 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <span className="text-sm font-medium text-purple-700">
                    {qtyInCart} no carrinho
                  </span>
                </div>
              )}

              {/* Badge de disponível (após subtração) */}
              {availableStock === 0 ? (
                <div className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
                  <svg
                    className="w-4 h-4 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  <span className="text-sm font-medium text-red-700">
                    Esgotado
                  </span>
                </div>
              ) : (
                <div
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg ${
                    availableStock <= 5
                      ? "bg-red-50 border border-red-200"
                      : availableStock <= 10
                      ? "bg-amber-50 border border-amber-200"
                      : "bg-green-50 border border-green-200"
                  }`}
                >
                  <svg
                    className={`w-4 h-4 ${
                      availableStock <= 5
                        ? "text-red-600"
                        : availableStock <= 10
                        ? "text-amber-600"
                        : "text-green-600"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span
                    className={`text-sm font-medium ${
                      availableStock <= 5
                        ? "text-red-700"
                        : availableStock <= 10
                        ? "text-amber-700"
                        : "text-green-700"
                    }`}
                  >
                    {availableStock <= 5
                      ? `Últimas ${availableStock}!`
                      : `${availableStock} disponível`}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="text-xl font-bold text-brand-primary">
          {formatBRL(total)}
        </div>
      </div>

      {/* quantidade principal (modo N) */}
      {creating === "N" && (
        <div className="flex items-center gap-3 mt-2 mb-4">
          <span className="text-sm text-gray-500">Quantidade</span>
          <button
            type="button"
            className="px-3 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 transition disabled:opacity-50"
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            disabled={qty <= 1 || availableStock === 0}
          >
            −
          </button>
          <span className="w-8 text-center">{qty}</span>
          <button
            type="button"
            disabled={qty >= availableStock || availableStock === 0}
            className={`px-3 py-1 rounded transition ${
              qty >= availableStock || availableStock === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-green-100 text-green-700 hover:bg-green-200"
            }`}
            onClick={() => setQty((q) => Math.min(q + 1, availableStock))}
            title={
              availableStock === 0
                ? "Estoque esgotado"
                : qty >= availableStock
                ? `Máximo disponível: ${availableStock}`
                : undefined
            }
          >
            +
          </button>
          {availableStock > 0 && qty >= availableStock && (
            <span className="text-xs text-amber-600 ml-2">
              Limite disponível atingido
            </span>
          )}
        </div>
      )}

      {/* modo S: personalização */}
      {creating === "S" ? (
        <>
          {/* TAMANHO (ingredientes) */}
          {hasExtraIngredients === "S" && (
            <div className="mt-4 mb-6">
              <h3 className="font-bold text-gray-800 mb-2">
                {sizes.map((s) => (
                  <span key={s.key}>{s.label}</span>
                ))}
              </h3>

              <p className="text-xs text-gray-500 mt-1">
                Este tamanho inclui{" "}
                <b>{sizes.find((s) => s.key === sizeKey)?.included ?? 0}</b>{" "}
                ingrediente(s) padrão. Excedentes são cobrados à parte.
              </p>
            </div>
          )}

          {/* INGREDIENTES BASE */}
          {hasExtraIngredients === "S" && (
            <div className="mb-6">
              <h3 className="font-bold text-gray-800 mb-2">
                Ingredientes do produto
              </h3>
              {baseIngredients.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum ingrediente.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {baseIngredients.map((ing) => {
                    const q = baseQty[ing.id] ?? 0;
                    const currentTotal = Object.values(baseQty).reduce(
                      (acc, qty) => acc + qty,
                      0
                    );
                    const included = Number(sizeInfo?.included ?? 0);
                    // Desabilita o botão + quando atingir o limite total
                    const canAdd = currentTotal < included;

                    // Itens base nunca são cobrados
                    const willCharge = false;

                    return (
                      <div
                        key={ing.id}
                        className={`flex items-center justify-between border rounded p-2 transition ${
                          q > 0
                            ? "bg-amber-50 border-amber-400"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{ing.name}</span>
                          <span className="text-xs text-gray-400">incluir</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 transition disabled:opacity-50"
                            onClick={() => decBase(ing.id)}
                            disabled={q === 0}
                          >
                            −
                          </button>
                          <span className="w-6 text-center">{q}</span>
                          <button
                            type="button"
                            className="px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 transition disabled:opacity-50"
                            onClick={() => incBase(ing.id)}
                            disabled={!canAdd}
                            title={
                              !canAdd
                                ? `Você já selecionou ${included} ingrediente(s) incluídos`
                                : undefined
                            }
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* INGREDIENTES EXTRAS (com + verde / - vermelho) */}
          {hasExtraIngredients === "S" && (
            <div className="mb-6">
              <h3 className="font-bold text-gray-800 mb-2">
                Ingredientes extras
              </h3>
              {extraIngredients.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Não há extras disponíveis.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {extraIngredients.map((ing) => {
                    const q = extraQty[ing.id] ?? 0;
                    return (
                      <div
                        key={ing.id}
                        className="flex items-center justify-between border rounded p-2"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{ing.name}</span>
                          <span className="text-xs text-emerald-700">
                            + {formatBRL(ing.price)} / un.
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 transition"
                            onClick={() => decExtra(ing.id)}
                          >
                            −
                          </button>
                          <span className="w-6 text-center">{q}</span>
                          <button
                            type="button"
                            className="px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 transition"
                            onClick={() => incExtra(ing.id)}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAMANHO (produtos) */}
          {hasExtraProducts === "S" && (
            <div className="mt-4 mb-6">
              <h3 className="font-bold text-gray-800 mb-2">
                {sizes.map((s) => (
                  <span key={s.key}>{s.label_product}</span>
                ))}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Este tamanho inclui{" "}
                <b>
                  {sizes.find((s) => s.key === sizeKey)?.includedProduct ?? 0}
                </b>{" "}
                produto(s) padrão. Excedentes são cobrados.
              </p>
            </div>
          )}

          {/* PRODUTOS BASE */}
          {hasExtraProducts === "S" && (
            <div className="mb-6">
              <h3 className="font-bold text-gray-800 mb-2">
                Produtos para Seleção
              </h3>
              {baseProdutos.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum produto.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {baseProdutos.map((prod) => {
                    const q = baseProductQty[prod.id] ?? 0;
                    const currentTotal = Object.values(baseProductQty).reduce(
                      (acc, qty) => acc + qty,
                      0
                    );
                    const included = Number(sizeInfo?.includedProduct ?? 0);
                    const maxQty = Number(prod.qty ?? 99);
                    const {
                      inCart: qtyInCart,
                      isOutOfStock,
                      remaining: disponivel,
                    } = getSubProductStock(prod.id, maxQty);

                    // Desabilita o botão + quando atingir a franquia do combo OU o estoque
                    const canAdd = currentTotal < included && disponivel > 0;

                    return (
                      <div
                        key={prod.id}
                        className={`flex items-center justify-between border rounded p-2 transition ${
                          isOutOfStock
                            ? "bg-gray-50 border-gray-300 opacity-60"
                            : q > 0
                            ? "bg-amber-50 border-amber-400"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                          <span
                            className={`font-medium truncate ${
                              isOutOfStock ? "text-gray-500" : "text-gray-800"
                            }`}
                          >
                            {prod.name}
                          </span>
                          {isOutOfStock && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-red-50 border border-red-200 text-xs font-medium text-red-700 whitespace-nowrap">
                              <svg
                                className="w-3 h-3 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                              Esgotado
                            </span>
                          )}
                          {qtyInCart > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-purple-50 border border-purple-200 text-xs font-medium text-purple-700 whitespace-nowrap">
                              <svg
                                className="w-3 h-3 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                                />
                              </svg>
                              {qtyInCart} no carrinho
                            </span>
                          )}
                          {!isOutOfStock && disponivel >= 0 && maxQty < 99 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-green-50 border border-green-200 text-xs font-medium text-green-700 whitespace-nowrap">
                              <svg
                                className="w-3 h-3 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              {disponivel} disponível
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => decBaseProduct(prod.id)}
                            disabled={q === 0}
                          >
                            −
                          </button>
                          <span className="w-6 text-center">{q}</span>
                          <button
                            type="button"
                            className="px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => incBaseProduct(prod.id)}
                            disabled={!canAdd || isOutOfStock}
                            title={
                              isOutOfStock
                                ? `Produto esgotado (${qtyInCart} no carrinho, estoque máximo: ${maxQty})`
                                : disponivel === 0
                                ? `Limite máximo deste produto: ${maxQty}${
                                    qtyInCart > 0
                                      ? ` (${qtyInCart} já no carrinho)`
                                      : ""
                                  }`
                                : !canAdd
                                ? `Você já selecionou ${included} produto(s) incluídos`
                                : undefined
                            }
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* PRODUTOS EXTRAS (com + verde / - vermelho) */}
          {hasExtraProducts === "S" && (
            <div className="mb-6">
              <h3 className="font-bold text-gray-800 mb-2">Produtos extras</h3>
              {extraProducts.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Não há extras disponíveis.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {extraProducts.map((ing) => {
                    const q = extraProductQty[ing.id] ?? 0;
                    const maxQty = Number(ing.qty ?? 99);
                    const {
                      inCart: qtyInCart,
                      isOutOfStock,
                      remaining: disponivel,
                    } = getSubProductStock(ing.id, maxQty);
                    const canAdd = disponivel > 0;

                    return (
                      <div
                        key={ing.id}
                        className={`flex items-center justify-between border rounded p-2 transition ${
                          isOutOfStock
                            ? "bg-gray-50 border-gray-300 opacity-60"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="flex flex-col flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={`font-medium truncate ${
                                isOutOfStock ? "text-gray-500" : "text-gray-800"
                              }`}
                            >
                              {ing.name}
                            </span>
                            {isOutOfStock && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-red-50 border border-red-200 text-xs font-medium text-red-700 whitespace-nowrap">
                                <svg
                                  className="w-3 h-3 mr-1"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                                Esgotado
                              </span>
                            )}
                            {qtyInCart > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-purple-50 border border-purple-200 text-xs font-medium text-purple-700 whitespace-nowrap">
                                <svg
                                  className="w-3 h-3 mr-1"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                                  />
                                </svg>
                                {qtyInCart} no carrinho
                              </span>
                            )}
                            {!isOutOfStock &&
                              disponivel >= 0 &&
                              maxQty < 99 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-green-50 border border-green-200 text-xs font-medium text-green-700 whitespace-nowrap">
                                  <svg
                                    className="w-3 h-3 mr-1"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                  {disponivel} disponível
                                </span>
                              )}
                          </div>
                          <span
                            className={`text-xs mt-0.5 ${
                              isOutOfStock
                                ? "text-gray-400"
                                : "text-emerald-700"
                            }`}
                          >
                            + {formatBRL(ing.price)} / un.
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => decExtraProduct(ing.id)}
                            disabled={q === 0}
                          >
                            −
                          </button>
                          <span className="w-6 text-center">{q}</span>
                          <button
                            type="button"
                            className="px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => incExtraProduct(ing.id)}
                            disabled={!canAdd || isOutOfStock}
                            title={
                              isOutOfStock
                                ? `Produto esgotado (${qtyInCart} no carrinho, estoque máximo: ${maxQty})`
                                : !canAdd
                                ? `Limite máximo: ${maxQty}${
                                    qtyInCart > 0
                                      ? ` (${qtyInCart} já no carrinho)`
                                      : ""
                                  }`
                                : undefined
                            }
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Observações */}
          <div className="mb-6">
            <label className="block font-bold text-gray-800 mb-2">
              Observações (opcional):
            </label>
            <textarea
              placeholder="Ex.: sem açúcar, menos doce, etc."
              className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>

          {/* 🎯 Feedback de validação do combo */}
          {!isComboValid && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-800 mb-1">
                    Complete a seleção do combo
                  </p>
                  <div className="text-xs text-amber-700 space-y-1">
                    {baseIncludedCount > 0 && (
                      <div>
                        <strong>Ingredientes:</strong>{" "}
                        {baseSelectedCount === baseIncludedCount ? (
                          <span className="text-green-700">✓ Completo</span>
                        ) : baseSelectedCount < baseIncludedCount ? (
                          <span>
                            Faltam {baseIncludedCount - baseSelectedCount} de{" "}
                            {baseIncludedCount}
                          </span>
                        ) : (
                          <span className="text-red-700">
                            Excedeu! Selecione apenas {baseIncludedCount}
                          </span>
                        )}
                      </div>
                    )}
                    {baseProductIncludedCount > 0 && (
                      <div>
                        <strong>Produtos:</strong>{" "}
                        {baseProductSelectedCount ===
                        baseProductIncludedCount ? (
                          <span className="text-green-700">✓ Completo</span>
                        ) : baseProductSelectedCount <
                          baseProductIncludedCount ? (
                          <span>
                            Faltam{" "}
                            {baseProductIncludedCount -
                              baseProductSelectedCount}{" "}
                            de {baseProductIncludedCount}
                          </span>
                        ) : (
                          <span className="text-red-700">
                            Excedeu! Selecione apenas {baseProductIncludedCount}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : null}

      {/* CTA */}
      <button
        type="button"
        onClick={handleAdd}
        disabled={creating === "S" && !isComboValid}
        className={`w-full py-3 px-6 rounded-xl font-bold text-lg transition mt-2 ${
          creating === "S" && !isComboValid
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-brand-primary hover:bg-brand-primary-dark text-white"
        }`}
        title={
          creating === "S" && !isComboValid
            ? "Complete a seleção do combo antes de adicionar ao carrinho"
            : undefined
        }
      >
        Adicionar ao Carrinho • {formatBRL(total)}
      </button>

      {/* MODAL BONITÃO DE CONFIRMAÇÃO */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Produto adicionado! 🎉
            </h3>
            <p className="text-gray-600 mb-6">
              Seu item foi adicionado ao carrinho com sucesso.
              <br />O que você deseja fazer agora?
            </p>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleConfirmContinue}
                className="w-full sm:w-auto px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
              >
                Continuar comprando
              </button>
              <button
                type="button"
                onClick={handleConfirmGoToCart}
                className="w-full sm:w-auto px-4 py-2 rounded-xl bg-green-500 hover:bg-green-600 text-white font-semibold transition"
              >
                Ir para o carrinho
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

ProductModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  product: PropTypes.object,
  creating: PropTypes.oneOf(["S", "N"]),
  hasExtraIngredients: PropTypes.oneOf(["S", "N"]),
  hasExtraProducts: PropTypes.oneOf(["S", "N"]),
  addToCart: PropTypes.func.isRequired,
  sizes: PropTypes.array,
  onOpenCart: PropTypes.func,
};

ProductModalBody.propTypes = {
  creating: PropTypes.oneOf(["S", "N"]).required,
  hasExtraIngredients: PropTypes.oneOf(["S", "N"]).required,
  hasExtraProducts: PropTypes.oneOf(["S", "N"]).required,
  product: PropTypes.object.isRequired,
  addToCart: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  sizes: PropTypes.array.isRequired,
  onOpenCart: PropTypes.func,
};
